import { formatNumeral, NumeralThousandGroupStyles, unformatNumeral } from 'cleave-zen';
import { useCallback, useEffect, useState } from 'react';

type Props = {
  precision?: number;
  digit?: number;
  displayPrecision?: boolean;
  displayPrecisionOnBlur?: boolean;
  inputRef: React.RefObject<HTMLInputElement>;

  onChange?: (value: number | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: number;
};

export const useFormatNumericDisplayValue = ({
  value,
  precision,
  digit,
  displayPrecision,
  displayPrecisionOnBlur,
  inputRef,
  onChange,
  onBlur,
}: Props) => {
  const [displayValue, setDisplayValue] = useState('');

  const formatDisplayValue = useCallback(
    (value?: number) => {
      const formattedValue = formatNumeral(value?.toString() ?? '', {
        numeralDecimalMark: '.',
        numeralDecimalScale: precision,
        numeralIntegerScale: digit,
        numeralThousandsGroupStyle: NumeralThousandGroupStyles.THOUSAND,
      });
      const numericValue = parseFloat(unformatNumeral(formattedValue));

      const isActiveElement = document.activeElement === inputRef.current;
      const shouldDisplayPrecision =
        !isActiveElement && (displayPrecision || displayPrecisionOnBlur);

      return {
        formattedValue: shouldDisplayPrecision ? numericValue.toFixed(precision) : formattedValue,
        numericValue: !isNaN(numericValue) ? numericValue : null,
      };
    },
    [precision, digit, inputRef, displayPrecision, displayPrecisionOnBlur]
  );

  const handleChange = rawValue => {
    const { formattedValue, numericValue } = formatDisplayValue(rawValue);

    setDisplayValue(formattedValue);

    if (numericValue !== value) {
      onChange && onChange(numericValue);
    }
  };

  useEffect(() => {
    const { formattedValue } = formatDisplayValue(value);
    setDisplayValue(formattedValue);
  }, [value, formatDisplayValue]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    if (displayPrecisionOnBlur) {
      setDisplayValue(formatDisplayValue(value).formattedValue);
    }
  };

  return {
    displayValue,
    handleChange,
    handleBlur,
  };
};
